import React from 'react';
import { UserPlus, Mail, ExternalLink } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

export default function CreateUserDialog({
  open,
  onOpenChange,
  userForm,
  setUserForm,
  onSubmit,
  isSuperAdmin,
  organizations,
  onViewOrgSettings,
}) {
  // Default the invitation toggle ON the first time the dialog opens
  // for this form. Admins can flip it off if they prefer to share the
  // temp password through their own channel (e.g. Teams DM).
  const sendInvitation = userForm.send_invitation !== false;
  const setSendInvitation = (next) => setUserForm({ ...userForm, send_invitation: next });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
          <DialogDescription>
            Provision a teammate's account with a temporary password. We'll
            email it to them and force a password change on first sign-in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium">Email Address</label>
            <Input
              type="email"
              placeholder="user@example.com"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              data-testid="create-user-email-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Full Name</label>
            <Input
              placeholder="John Doe"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              data-testid="create-user-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Temporary Password</label>
            <Input
              type="text"
              placeholder="Temp123!"
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              data-testid="create-user-password-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              The teammate will be forced to choose their own on first sign-in.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium">Role</label>
            <Select
              value={userForm.role}
              onValueChange={(v) => setUserForm({ ...userForm, role: v })}
            >
              <SelectTrigger data-testid="create-user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isSuperAdmin && (
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                )}
                <SelectItem value="org_admin">Organization Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isSuperAdmin ? (
            <div>
              <label className="text-sm font-medium">Organization</label>
              <Select
                value={userForm.organization_id}
                onValueChange={(v) => setUserForm({ ...userForm, organization_id: v })}
              >
                <SelectTrigger data-testid="create-user-org-select">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            // Org admins create users INTO their own org. We render a
            // clickable badge that doubles as a tiny breadcrumb —
            // hovering shows "View org settings", clicking jumps to
            // the Organizations tab pre-filtered to this tenant.
            <div>
              <label className="text-sm font-medium">Organization</label>
              <button
                type="button"
                onClick={onViewOrgSettings}
                disabled={!onViewOrgSettings}
                className={`mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700 transition ${
                  onViewOrgSettings
                    ? 'hover:bg-slate-100 hover:border-slate-300 cursor-pointer'
                    : 'cursor-default'
                }`}
                data-testid="create-user-org-readonly"
                title={onViewOrgSettings ? 'Open organization settings' : 'Scoped automatically to your org'}
              >
                <span className="font-medium">
                  {organizations.find(o => o.id === userForm.organization_id)?.name
                    || userForm.organization_id || 'Your organization'}
                </span>
                <span className="text-xs text-slate-500 ml-auto flex items-center gap-1">
                  scoped automatically
                  {onViewOrgSettings && <ExternalLink className="h-3 w-3" />}
                </span>
              </button>
            </div>
          )}
          <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
            <Mail className="h-5 w-5 text-teal-600 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor="create-user-invite-toggle"
                  className="text-sm font-medium text-slate-800"
                >
                  Email invitation to teammate
                </label>
                <Switch
                  id="create-user-invite-toggle"
                  checked={sendInvitation}
                  onCheckedChange={setSendInvitation}
                  data-testid="create-user-send-invitation-toggle"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Sends a Resend email containing the temp password and a sign-in link.
                Turn off if you'd rather share credentials yourself.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} data-testid="create-user-submit-btn">
            <UserPlus size={16} className="mr-2" />
            Create User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
